import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAzureRegions, fetchAzureCatalog } from '../store/actions/cluster.actions';
import { resolveStackImage, imageShapeCompatibility, quotaShortfall, quotaReason, imageInRegion } from './imageCompat';

/**
 * Live Azure data for the credential-first cluster form.
 *
 * Pick a credential -> the subscription's regions are fetched.
 * Pick a region     -> VM sizes with their capabilities, the per-family vCPU
 *                      quota, the credential's Heidi images and on-demand
 *                      prices are fetched together (about two seconds).
 *
 * Everything the selectors need to make a bad choice unselectable is derived
 * here, and every predicate answers `null` ("don't know") until the catalog
 * for the current credential + region has actually arrived, so nothing is
 * greyed out on stale or missing data.
 *
 * There is no zone here on purpose: Azure clusters place regionally, so the
 * question a size has to answer is whether the subscription can use it in the
 * region at all, and whether the cluster fits the quota.
 */

const EMPTY = [];

// Shape the live catalog entry the way the shared InstanceSizeSelector already
// reads shapes (it probes vcpus / memory / gpus / gpu_type / architecture), so
// the selector needs no Azure-specific knowledge.
function toSelectorShape(shape, prices) {
    return {
        instance_id: shape.instance_id,
        name: shape.instance_id,
        vcpus: shape.vcpus,
        cores: String(shape.vcpus),
        memory: shape.memGiB,
        gpus: shape.gpu ? shape.gpu.count : undefined,
        gpu_type: shape.gpu ? shape.gpu.name : undefined,
        architecture: shape.arch,
        archs: shape.archs || [shape.arch],
        // What an image must look like to boot on this size (see imageCompat.js).
        arch: shape.arch,
        hyperVGenerations: shape.hyperVGenerations || [],
        family: shape.family,
        available: shape.available !== false,
        acceleratedNetworking: shape.acceleratedNetworking,
        capacityReservationSupported: shape.capacityReservationSupported,
        price: prices && prices[shape.instance_id] != null ? prices[shape.instance_id] : null,
    };
}

export default function useAzureCatalog({ credentialId, regionId, enabled = true }) {
    const dispatch = useDispatch();
    const regionsState = useSelector(({ clusters }) => clusters.cluster.azureCatalog.regions);
    const catalogState = useSelector(({ clusters }) => clusters.cluster.azureCatalog.catalog);

    // Fetch regions whenever the credential changes.
    useEffect(() => {
        if (!enabled || !credentialId) return;
        if (regionsState.credentialId === credentialId && (regionsState.loading || regionsState.list.length || regionsState.error)) return;
        dispatch(fetchAzureRegions(credentialId));
    }, [dispatch, enabled, credentialId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch the catalog whenever credential + region change.
    const requestKey = credentialId && regionId ? `${credentialId}|${regionId}` : null;
    useEffect(() => {
        if (!enabled || !requestKey) return;
        if (catalogState.requestKey === requestKey) return;
        dispatch(fetchAzureCatalog(credentialId, regionId));
    }, [dispatch, enabled, requestKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const retry = useCallback(() => {
        if (credentialId) dispatch(fetchAzureRegions(credentialId));
        if (credentialId && regionId) dispatch(fetchAzureCatalog(credentialId, regionId));
    }, [dispatch, credentialId, regionId]);

    // --- Regions ---------------------------------------------------------
    const regionsForCredential = regionsState.credentialId === credentialId;
    const regions = useMemo(() => (
        regionsForCredential
            ? regionsState.list
                .map((r) => ({
                    region_id: r.region_id,
                    // "eastus - East US (Virginia)" reads better than either half alone.
                    name: r.physicalLocation ? `${r.region_id} - ${r.name} (${r.physicalLocation})` : `${r.region_id} - ${r.name}`,
                    // Azure's own geography, so the dropdown groups by continent.
                    group: r.geographyGroup || 'Other',
                    recommended: r.recommended,
                }))
                // Within a geography, Azure's mainstream regions come first; the
                // rest are canary, staging or restricted.
                .sort((a, b) => (a.recommended === b.recommended
                    ? a.region_id.localeCompare(b.region_id)
                    : (a.recommended ? -1 : 1)))
            : EMPTY
    ), [regionsForCredential, regionsState.list]);

    // --- Catalog ---------------------------------------------------------
    const data = catalogState.requestKey === requestKey ? catalogState.data : null;
    const catalogReady = Boolean(requestKey && data && !catalogState.loading && !catalogState.error);
    const catalogLoading = Boolean(requestKey && catalogState.requestKey === requestKey && catalogState.loading);
    const catalogError = requestKey && catalogState.requestKey === requestKey ? catalogState.error : null;

    const shapes = useMemo(() => (data ? data.shapes.map((s) => toSelectorShape(s, data.prices)) : EMPTY), [data]);
    const shapeById = useCallback((instanceId) => shapes.find((s) => s.instance_id === instanceId) || null, [shapes]);

    // Images usable in this region (null = could not list).
    const images = data && Array.isArray(data.images) ? data.images : null;
    const imagesReady = catalogReady && Array.isArray(images);
    const imageById = useCallback((imageId) => (images ? images.find((i) => i.imageId === imageId) || null : null), [images]);
    const resolveImage = useCallback(
        (imageName, kind) => (images ? resolveStackImage(images, imageName, kind, regionId) : null),
        [images, regionId]
    );

    const quota = data ? data.quota || {} : {};

    /**
     * vCPUs this cluster would consume, per family. Counted across the whole
     * cluster because two groups of the same family compete for one limit.
     */
    const coresByFamily = useCallback((roles) => {
        const totals = {};
        (roles || []).forEach((role) => {
            const shape = shapeById(role.instance_id);
            if (!shape || !shape.family || !shape.vcpus) return;
            totals[shape.family] = (totals[shape.family] || 0) + shape.vcpus * (role.count || 1);
        });
        return totals;
    }, [shapeById]);

    /**
     * Size dropdown row. Disabled when the subscription cannot use it here,
     * when the cluster would not fit the family's quota, or when the chosen
     * image cannot boot on it.
     *
     * `roles` is every role the cluster provisions, so the quota answer
     * accounts for the whole cluster rather than this row alone.
     */
    const instanceOptionState = useCallback((instanceId, { image, roles, terms, productName } = {}) => {
        if (!catalogReady) return { disabled: false, reason: null };
        const shape = shapeById(instanceId);
        if (!shape) return { disabled: false, reason: null };

        if (shape.available === false) {
            return { disabled: true, reason: `Not available to this subscription in ${regionId}` };
        }

        if (image) {
            const verdict = imageShapeCompatibility(image, shape, { terms, productName });
            if (!verdict.compatible) return { disabled: true, reason: verdict.shapeReason };
        }

        // What the cluster would need if this row were set to this size.
        const projected = (roles || []).map((r) => (r.self ? { ...r, instance_id: instanceId } : r));
        const needed = coresByFamily(projected)[shape.family] || (shape.vcpus || 0);
        const short = quotaShortfall(quota, shape, needed);
        if (short) return { disabled: true, reason: quotaReason(short) };

        return { disabled: false, reason: null };
    }, [catalogReady, shapeById, regionId, quota, coresByFamily]);

    /** Image dropdown row: disabled when any role's size cannot boot it. */
    const imageOptionState = useCallback((image, { roles, terms, productName } = {}) => {
        if (!catalogReady || !image) return { disabled: false, reason: null };
        for (const role of roles || []) {
            const shape = shapeById(role.instance_id);
            if (!shape) continue;
            const verdict = imageShapeCompatibility(image, shape, { terms, productName });
            if (!verdict.compatible) {
                return { disabled: true, reason: `${verdict.imageReason} (${role.role})` };
            }
        }
        return { disabled: false, reason: null };
    }, [catalogReady, shapeById]);

    const priceOf = useCallback((instanceId) => (
        data && data.prices && data.prices[instanceId] != null ? data.prices[instanceId] : null
    ), [data]);

    /**
     * Azure has no "enable this region" switch the way AWS does, so a region
     * the subscription cannot actually use still appears in the list - it just
     * has every VM size Location-restricted. brazilsoutheast is one: 754 sizes
     * listed, none usable. Say so on the region rather than showing an empty
     * size dropdown.
     */
    const regionUnusable = catalogReady && shapes.length > 0 && shapes.every((s) => s.available === false);

    return useMemo(() => ({
        regions,
        regionUnusable,
        regionsLoading: regionsForCredential && regionsState.loading,
        regionsError: regionsForCredential ? regionsState.error : null,
        shapes,
        catalogReady, catalogLoading, catalogError,
        pricing: data ? data.pricing : null,
        quota, quotaStatus: data ? data.quotaStatus : null,
        priceOf,
        images, imagesReady, imageCatalog: data ? data.imageCatalog : null,
        imageById, resolveImage, shapeById,
        instanceOptionState, imageOptionState,
        coresByFamily,
        imageInRegion,
        retry,
    }), [regions, regionUnusable, regionsForCredential, regionsState.loading, regionsState.error, shapes,
        catalogReady, catalogLoading, catalogError, data, quota, priceOf, images, imagesReady,
        imageById, resolveImage, shapeById, instanceOptionState, imageOptionState, coresByFamily, retry]);
}
